import {
    Button,
    CircularProgress,
    DatePicker,
    Grid,
    InputAdornment,
    Modal,
    ModalContent,
    ModalTitle,
    Option,
    SelectField,
} from '@deere/fuel-react';
import { ReactComponent as ClockIcon } from '@deere/ux.brand-foundations/icons/history.svg';
import { DateTime } from 'luxon';
import { PropTypes } from 'prop-types';
import { useContext, useEffect, useState } from 'react';
import { AlertBox } from './AlertBox';
import isEqual from 'lodash.isequal';

import { BidCycleCalendar } from './BidCycleCalendar';
import styles from './BidCycleSettingsForm.module.css';
import { LoggedInContext } from '../context/LoggedInContext';
import { errorBannerFromResponse } from '../utils/errorBanner';

const WEEKDAYS_FULL = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
];
const BID_CYCLE_OPTIONS = [
    { label: 'Weekly', value: 'EVERY_WEEK' },
    { label: '1st & 3rd of the month', value: 'FIRST_AND_THIRD_WEEK' },
    { label: '2nd & 4th of the month', value: 'SECOND_AND_FOURTH_WEEK' },
];
const DAYS_ACTIVE_OPTIONS = ['2', '3', '4', '5', '6', '7'];
const DAYS_TO_WITHDRAW_OPTIONS = [
    '0 days',
    '1 day',
    '2 days',
    '3 days',
    '4 days',
    '5 days',
    '6 days',
    '7 days',
];

export const BidCycleSettingsForm = ({
    biddingGroup,
    modalIsOpen,
    onCloseModal,
    updateBidCycle,
}) => {
    const [errorMessage, setErrorMessage] = useState(null);
    const [isSavingBidCycle, setIsSavingBidCycle] = useState(false);
    const { makeRequest } = useContext(LoggedInContext);
    const [timeToCloseHasChanged, setTimeToCloseHasChanged] = useState(false);
    const [timeToGoActiveHasChanged, setTimeToGoActiveHasChanged] =
        useState(false);

    const [bidCycle, setBidCycle] = useState(() => ({
        id: biddingGroup.id,
        daysInActivePeriod: biddingGroup.daysInActivePeriod
            ? parseInt(biddingGroup.daysInActivePeriod)
            : '',
        daysInWithdrawalPeriod:
            biddingGroup.daysInWithdrawalPeriod !== null &&
            biddingGroup.daysInWithdrawalPeriod !== undefined
                ? DAYS_TO_WITHDRAW_OPTIONS[biddingGroup.daysInWithdrawalPeriod]
                : '',
        dayToGoActive: biddingGroup.dayToGoActive
            ? WEEKDAYS_FULL[biddingGroup.dayToGoActive]
            : WEEKDAYS_FULL[1],
        bidCycleInterval: biddingGroup.bidCycleInterval
            ? biddingGroup.bidCycleInterval
            : '',
        timeToGoActive: biddingGroup.timeToGoActive
            ? DateTime.fromISO(biddingGroup.timeToGoActive).toFormat('t')
            : '',
        timeToClose: biddingGroup.timeToClose
            ? DateTime.fromISO(biddingGroup.timeToClose).toFormat('t')
            : '',
    }));

    const [prevBiddingGroup, setPrevBiddingGroup] = useState(null);

    useEffect(() => {
        if (!isEqual(biddingGroup, prevBiddingGroup)) {
            setBidCycle({
                id: biddingGroup.id || '',
                daysInActivePeriod: biddingGroup.daysInActivePeriod
                    ? parseInt(biddingGroup.daysInActivePeriod)
                    : '',
                daysInWithdrawalPeriod:
                    biddingGroup.daysInWithdrawalPeriod !== null &&
                    biddingGroup.daysInWithdrawalPeriod !== undefined
                        ? DAYS_TO_WITHDRAW_OPTIONS[
                              biddingGroup.daysInWithdrawalPeriod
                          ]
                        : '',
                dayToGoActive: biddingGroup.dayToGoActive
                    ? WEEKDAYS_FULL[biddingGroup.dayToGoActive]
                    : WEEKDAYS_FULL[1],
                bidCycleInterval: biddingGroup.bidCycleInterval || '',
                timeToGoActive: biddingGroup.timeToGoActive
                    ? DateTime.fromISO(biddingGroup.timeToGoActive).toFormat(
                          't'
                      )
                    : '',
                timeToClose: biddingGroup.timeToClose
                    ? DateTime.fromISO(biddingGroup.timeToClose).toFormat('t')
                    : '',
            });
            setPrevBiddingGroup(biddingGroup);
        }
    }, [biddingGroup, prevBiddingGroup]);

    const isWeeklyBidCyclesInvalid = (
        bidCycleInterval,
        daysActive,
        daysInWithdrawal
    ) => {
        if (
            bidCycleInterval === 'EVERY_WEEK' &&
            parseInt(daysActive) + parseInt(daysInWithdrawal) >
                WEEKDAYS_FULL.length
        ) {
            setErrorMessage(
                'Bid cycles cannot overlap. You must adjust either the active and withdraw days or the number of cycles.'
            );
            return true;
        }
        setErrorMessage(null);
        return false;
    };

    const isDayToGoActiveInputInvalid = (day) => {
        return day === null || day === '';
    };

    const dayToGoActiveHandler = (event) => {
        const updatedDayOfWeek = event.target.value;
        setBidCycle({
            ...bidCycle,
            dayToGoActive: updatedDayOfWeek,
            dayToGoActiveIsInvalid:
                isDayToGoActiveInputInvalid(updatedDayOfWeek),
        });
    };

    const isBidCycleIntervalInputInvalid = (
        interval,
        invalidWeeklyBidCycle
    ) => {
        return interval === null || interval === '' || invalidWeeklyBidCycle;
    };

    const bidCycleIntervalHandler = (event) => {
        const updateBidCycleInterval = event.target.value;
        const mismatch = isWeeklyBidCyclesInvalid(
            updateBidCycleInterval,
            bidCycle.daysInActivePeriod,
            bidCycle.daysInWithdrawalPeriod
        );
        if (mismatch) {
            setBidCycle({
                ...bidCycle,
                bidCycleInterval: updateBidCycleInterval,
                bidCycleIntervalIsInvalid: true,
                daysActiveIsInvalid: true,
                daysToWithdrawIsInvalid: true,
            });
        } else {
            setBidCycle({
                ...bidCycle,
                bidCycleInterval: updateBidCycleInterval,
                bidCycleIntervalIsInvalid: isBidCycleIntervalInputInvalid(
                    updateBidCycleInterval,
                    false
                ),
                daysActiveIsInvalid: isDaysInActivePeriodInputInvalid(
                    bidCycle.daysInActivePeriod,
                    false
                ),
                daysToWithdrawIsInvalid: isDaysInWithdrawalPeriodInputInvalid(
                    bidCycle.daysInWithdrawalPeriod,
                    false
                ),
            });
        }
    };

    const isDaysInActivePeriodInputInvalid = (
        numberOfDays,
        invalidWeeklyBidCycle
    ) => {
        return (
            numberOfDays === null ||
            numberOfDays === '' ||
            invalidWeeklyBidCycle
        );
    };

    const daysInActivePeriodHandler = (event) => {
        const numberOfDays = event.target.value;
        const mismatch = isWeeklyBidCyclesInvalid(
            bidCycle.bidCycleInterval,
            numberOfDays,
            bidCycle.daysInWithdrawalPeriod
        );
        if (mismatch) {
            setBidCycle({
                ...bidCycle,
                daysInActivePeriod: numberOfDays,
                bidCycleIntervalIsInvalid: true,
                daysActiveIsInvalid: true,
                daysToWithdrawIsInvalid: true,
            });
        } else {
            setBidCycle({
                ...bidCycle,
                daysInActivePeriod: numberOfDays,
                daysActiveIsInvalid: isDaysInActivePeriodInputInvalid(
                    numberOfDays,
                    false
                ),
                daysToWithdrawIsInvalid: isDaysInWithdrawalPeriodInputInvalid(
                    bidCycle.daysInWithdrawalPeriod,
                    false
                ),
                bidCycleIntervalIsInvalid: isBidCycleIntervalInputInvalid(
                    bidCycle.bidCycleInterval,
                    false
                ),
            });
        }
    };

    const isDaysInWithdrawalPeriodInputInvalid = (
        numberOfDays,
        invalidWeeklyBidCycle
    ) => {
        return (
            numberOfDays === null ||
            numberOfDays === '' ||
            invalidWeeklyBidCycle
        );
    };

    const daysInWithdrawalPeriodHandler = (event) => {
        const numberOfDays = event.target.value;
        const mismatch = isWeeklyBidCyclesInvalid(
            bidCycle.bidCycleInterval,
            bidCycle.daysInActivePeriod,
            numberOfDays
        );
        if (mismatch) {
            setBidCycle({
                ...bidCycle,
                daysInWithdrawalPeriod: numberOfDays,
                bidCycleIntervalIsInvalid: true,
                daysActiveIsInvalid: true,
                daysToWithdrawIsInvalid: true,
            });
        } else {
            setBidCycle({
                ...bidCycle,
                daysInWithdrawalPeriod: numberOfDays,
                daysToWithdrawIsInvalid: isDaysInWithdrawalPeriodInputInvalid(
                    numberOfDays,
                    false
                ),
                daysActiveIsInvalid: isDaysInActivePeriodInputInvalid(
                    bidCycle.daysInActivePeriod,
                    false
                ),
                bidCycleIntervalIsInvalid: isBidCycleIntervalInputInvalid(
                    bidCycle.bidCycleInterval,
                    false
                ),
            });
        }
    };

    const isTimeInputInvalid = (time) => {
        return time === null || time === '' || time === undefined;
    };

    const bidActiveTimeHandler = (date) => {
        setTimeToGoActiveHasChanged(true);
        const updatedTime = date[0] ?? '';
        setBidCycle({
            ...bidCycle,
            timeToGoActive: updatedTime,
            timeToGoActiveIsInvalid: isTimeInputInvalid(updatedTime),
        });
    };

    const bidCloseTimeHandler = (date) => {
        setTimeToCloseHasChanged(true);
        const updatedTime = date[0] ?? '';
        setBidCycle({
            ...bidCycle,
            timeToClose: date[0],
            timeToCloseIsInvalid: isTimeInputInvalid(updatedTime),
        });
    };

    const handleValidation = () => {
        const invalidWeeklyBidCycle = isWeeklyBidCyclesInvalid(
            bidCycle.bidCycleInterval,
            bidCycle.daysInActivePeriod,
            bidCycle.daysInWithdrawalPeriod
        );
        const validated = {
            ...bidCycle,
            dayToGoActiveIsInvalid: isDayToGoActiveInputInvalid(
                bidCycle.dayToGoActive
            ),
            bidCycleIntervalIsInvalid: isBidCycleIntervalInputInvalid(
                bidCycle.bidCycleInterval,
                invalidWeeklyBidCycle
            ),
            daysActiveIsInvalid: isDaysInActivePeriodInputInvalid(
                bidCycle.daysInActivePeriod,
                invalidWeeklyBidCycle
            ),
            daysToWithdrawIsInvalid: isDaysInWithdrawalPeriodInputInvalid(
                bidCycle.daysInWithdrawalPeriod,
                invalidWeeklyBidCycle
            ),
            timeToGoActiveIsInvalid: isTimeInputInvalid(
                bidCycle.timeToGoActive
            ),
            timeToCloseIsInvalid: isTimeInputInvalid(bidCycle.timeToClose),
        };
        setBidCycle(validated);
        const atLeastOneFieldIsNotValid =
            validated.dayToGoActiveIsInvalid ||
            validated.bidCycleIntervalIsInvalid ||
            validated.daysActiveIsInvalid ||
            validated.daysToWithdrawIsInvalid ||
            validated.timeToGoActiveIsInvalid ||
            validated.timeToCloseIsInvalid;
        if (invalidWeeklyBidCycle) {
            setErrorMessage('Bid cycles cannot overlap');
            return false;
        } else if (atLeastOneFieldIsNotValid) {
            setErrorMessage('You must fill out all required fields to save.');
            return false;
        }
        return true;
    };

    const convertTimeString = (timeString) => {
        const parsedTime = DateTime.fromISO(timeString, { zone: 'utc' });
        const adjustedTime = parsedTime.setZone(DateTime.local().zoneName);
        const finalTime = adjustedTime.set({
            second: 0,
            millisecond: 0,
        });
        const formattedTime = finalTime.toFormat('HH:mm:ss.SSSZZ');
        return formattedTime;
    };

    const saveHandler = async () => {
        setIsSavingBidCycle(true);
        if (!handleValidation()) {
            setIsSavingBidCycle(false);
            return;
        }
        const payload = {
            id: bidCycle.id,
            dayToGoActive: WEEKDAYS_FULL.indexOf(bidCycle.dayToGoActive),
            bidCycleInterval: bidCycle.bidCycleInterval,
            daysInActivePeriod: parseInt(bidCycle.daysInActivePeriod),
            daysInWithdrawalPeriod: DAYS_TO_WITHDRAW_OPTIONS.indexOf(
                bidCycle.daysInWithdrawalPeriod
            ),
            timeToGoActive: DateTime.fromJSDate(
                bidCycle.timeToGoActive
            ).toISOTime(),
            timeToClose: DateTime.fromJSDate(bidCycle.timeToClose).toISOTime(),
        };
        if (biddingGroup.timeToClose !== null && !timeToCloseHasChanged) {
            payload.timeToClose = convertTimeString(biddingGroup.timeToClose);
        }
        if (biddingGroup.timeToGoActive !== null && !timeToGoActiveHasChanged) {
            payload.timeToGoActive = convertTimeString(
                biddingGroup.timeToGoActive
            );
        }
        const requestOptions = {
            method: 'POST',
            body: JSON.stringify(payload),
        };
        const response = await makeRequest(
            `${process.env.REACT_APP_API_ENDPOINT}/settings/bid-group/${biddingGroup.id}/cycle`,
            requestOptions
        );
        if (response.ok) {
            const receivedBidCycle = await response.json();
            updateBidCycle(receivedBidCycle);
            onCloseModal();
        } else {
            setErrorMessage(
                errorBannerFromResponse(
                    response,
                    'Your bid cycle was not saved. Please try again later.'
                ).message
            );
        }
        setIsSavingBidCycle(false);
    };

    const closeModal = () => {
        setErrorMessage(null);
        onCloseModal();
    };

    return (
        <Modal open={modalIsOpen} onClose={closeModal}>
            <ModalTitle>
                {`${biddingGroup.displayName} Job Bid Cycle Length`}
            </ModalTitle>
            <ModalContent>
                <Grid container className={styles.cycleSettingsContainer}>
                    {errorMessage && (
                        <AlertBox variant={'error'} msg={errorMessage} />
                    )}
                    <Grid item>
                        The minimum number of bid cycles per month is 2. You are
                        not required to post new job bid listings on every
                        cycle. Following the closure of a bid, bid withdrawal is
                        a period of time when employees can remove their bids on
                        jobs they no longer want.
                    </Grid>
                    <Grid item sm={6}>
                        {/* number of days job bid is active */}
                        <SelectField
                            required
                            label={'Number of days Job Bid is Active'}
                            name={'Number of days Job Bid is Active'}
                            value={bidCycle.daysInActivePeriod}
                            error={bidCycle.daysActiveIsInvalid}
                            onChange={daysInActivePeriodHandler}
                        >
                            <Option disabled hidden value="">
                                Choose days active
                            </Option>
                            {DAYS_ACTIVE_OPTIONS.map((opt, i) => {
                                return (
                                    <Option value={opt} key={i}>
                                        {`${opt} days (may include weekends)`}
                                    </Option>
                                );
                            })}
                        </SelectField>
                    </Grid>
                    <Grid item sm={6}>
                        {/* number of days to withdraw */}
                        <SelectField
                            required
                            label={'Number of Days to Withdraw Bid'}
                            name={'Number of Days to Withdraw Bid'}
                            value={bidCycle.daysInWithdrawalPeriod}
                            error={bidCycle.daysToWithdrawIsInvalid}
                            onChange={daysInWithdrawalPeriodHandler}
                        >
                            <Option disabled hidden value="">
                                Choose days to withdraw
                            </Option>
                            {DAYS_TO_WITHDRAW_OPTIONS.map((opt, i) => {
                                return (
                                    <Option value={opt} key={i}>
                                        {opt}
                                    </Option>
                                );
                            })}
                        </SelectField>
                    </Grid>
                    <Grid item sm={6}>
                        <SelectField
                            required
                            label={'Job Bids Go Active On'}
                            name={'Job Bids Go Active On'}
                            value={bidCycle.dayToGoActive}
                            error={bidCycle.dayToGoActiveIsInvalid}
                            onChange={dayToGoActiveHandler}
                        >
                            {WEEKDAYS_FULL.map((day, i) => {
                                return (
                                    <Option value={day} key={i}>
                                        {day}
                                    </Option>
                                );
                            })}
                        </SelectField>
                    </Grid>
                    <Grid item sm={6}>
                        {/* Number of Bid Cycles per Month */}
                        <SelectField
                            required
                            label={'Number of Bid Cycles per Month'}
                            name={'Number of Bid Cycles per Month'}
                            value={bidCycle.bidCycleInterval}
                            error={bidCycle.bidCycleIntervalIsInvalid}
                            onChange={bidCycleIntervalHandler}
                        >
                            <Option disabled hidden value="">
                                Choose number of cycles
                            </Option>
                            {BID_CYCLE_OPTIONS.map((option, i) => {
                                return (
                                    <Option
                                        value={option.value}
                                        key={i}
                                        disabled={option.disabled}
                                    >
                                        {option.label}
                                    </Option>
                                );
                            })}
                        </SelectField>
                    </Grid>
                    <Grid item sm={6}>
                        {/* job active time*/}
                        <DatePicker
                            required
                            enableTime
                            InputProps={{
                                label: 'Bid Active Time',
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <ClockIcon />
                                    </InputAdornment>
                                ),
                                required: true,
                                error: bidCycle.timeToGoActiveIsInvalid,
                            }}
                            onChange={bidActiveTimeHandler}
                            noCalendar
                            value={bidCycle.timeToGoActive}
                        />
                    </Grid>
                    <Grid item sm={6}>
                        {/* job close time*/}
                        <DatePicker
                            required
                            enableTime
                            InputProps={{
                                label: 'Time of Bid Close',
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <ClockIcon />
                                    </InputAdornment>
                                ),
                                required: true,
                                error: bidCycle.timeToCloseIsInvalid,
                            }}
                            onChange={bidCloseTimeHandler}
                            noCalendar
                            value={bidCycle.timeToClose}
                        />
                    </Grid>
                    <BidCycleCalendar
                        dayToGoActive={bidCycle.dayToGoActive}
                        daysInActivePeriod={parseInt(
                            bidCycle.daysInActivePeriod
                        )}
                        daysInWithdrawalPeriod={parseInt(
                            bidCycle.daysInWithdrawalPeriod
                        )}
                        interval={bidCycle.bidCycleInterval}
                    />
                    <Grid item sm={10} className={styles.important}>
                        *Clicking Save will permanently save your choices
                    </Grid>
                    <Grid item sm={2}>
                        <Button
                            variant="primary"
                            onClick={saveHandler}
                            disabled={isSavingBidCycle}
                        >
                            {isSavingBidCycle ? (
                                <CircularProgress
                                    variant="indeterminate"
                                    title={'Saving Bid Cycle'}
                                />
                            ) : (
                                'Save'
                            )}
                        </Button>
                    </Grid>
                </Grid>
            </ModalContent>
        </Modal>
    );
};

BidCycleSettingsForm.propTypes = {
    biddingGroup: PropTypes.object.isRequired,
    modalIsOpen: PropTypes.bool.isRequired,
    onCloseModal: PropTypes.func.isRequired,
    updateBidCycle: PropTypes.func,
};
